// Send later (U12), pure parts: the presets (tomorrow 8:30, this afternoon 14:00, Monday 8:30),
// a time in words ("tomorrow at 8:30"), the date-time picker's wall-clock value, and reading a
// typed phrase ("tomorrow 8:30"), all on the owner's clock (their saved time zone, which may not
// be this browser's). Wall-clock arithmetic with Intl only, as server/calendar/time.ts does it.

const MINUTE = 60_000;
const DAY = 86_400_000;
/** A custom time must leave at least this long (the server also keeps the undo window). */
export const MIN_LEAD_MS = 2 * MINUTE;
export const MAX_AHEAD_DAYS = 365;
/** The default minute of a day picked without a time (and of the morning presets): 8:30. */
export const MORNING_MINUTE = 8 * 60 + 30;
const AFTERNOON_MINUTE = 14 * 60;

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface LocalDate {
  year: number;
  month: number;
  day: number;
}

interface LocalParts extends LocalDate {
  hour: number;
  minute: number;
  /** 0 = Sunday. */
  weekday: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

export function localParts(ms: number, timeZone: string): LocalParts {
  const p: Record<string, number> = {};
  for (const part of formatter(timeZone).formatToParts(new Date(ms)))
    if (part.type !== "literal") p[part.type] = Number(part.value);
  const date = { year: p.year!, month: p.month!, day: p.day! };
  return {
    ...date,
    hour: p.hour! % 24,
    minute: p.minute!,
    weekday: new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay(),
  };
}

function offsetMs(ms: number, timeZone: string): number {
  const p = localParts(ms, timeZone);
  const second = Math.floor(ms / 1000) % 60;
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** The instant of `minuteOfDay` on local date `d` in `timeZone` (DST-safe, like the server's). */
export function zonedTimeToUtc(d: LocalDate, minuteOfDay: number, timeZone: string): number {
  const guess = Date.UTC(d.year, d.month - 1, d.day, 0, minuteOfDay);
  const first = offsetMs(guess, timeZone);
  let t = guess - first;
  const second = offsetMs(t, timeZone);
  if (second !== first) t = guess - second;
  return t;
}

export function addDays(d: LocalDate, n: number): LocalDate {
  const t = new Date(Date.UTC(d.year, d.month - 1, d.day + n));
  return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() };
}

const sameDate = (a: LocalDate, b: LocalDate) =>
  a.year === b.year && a.month === b.month && a.day === b.day;

/** "8:30", "14:00": the app's 24-hour clock, no leading zero. */
export function clockWords(ms: number, timeZone: string): string {
  const p = localParts(ms, timeZone);
  return `${p.hour}:${String(p.minute).padStart(2, "0")}`;
}

/** "today at 14:00", "tomorrow at 8:30", "Monday at 8:30", "Tue 3 Nov at 8:30". */
export function sendAtWords(at: string, now: number, timeZone: string): string {
  const ms = Date.parse(at);
  const target = localParts(ms, timeZone);
  const today = localParts(now, timeZone);
  const clock = clockWords(ms, timeZone);
  if (sameDate(target, today)) return `today at ${clock}`;
  if (sameDate(target, addDays(today, 1))) return `tomorrow at ${clock}`;
  for (let n = 2; n < 7; n++)
    if (sameDate(target, addDays(today, n))) return `${WEEKDAYS[target.weekday]} at ${clock}`;
  const day = `${WEEKDAYS[target.weekday]!.slice(0, 3)} ${target.day} ${MONTHS[target.month - 1]}`;
  return `${day} at ${clock}`;
}

export const capitalize = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

export interface SendPreset {
  id: "tomorrow" | "afternoon" | "monday";
  label: string;
  at: string;
}

/**
 * Tomorrow at 8:30; this afternoon at 14:00 while that is still half an hour away; Monday at 8:30
 * unless that is tomorrow (the next Monday when today is Monday).
 */
export function sendPresets(now: number, timeZone: string): SendPreset[] {
  const today = localParts(now, timeZone);
  const iso = (d: LocalDate, minute: number) => new Date(zonedTimeToUtc(d, minute, timeZone));
  const out: SendPreset[] = [];
  const afternoon = iso(today, AFTERNOON_MINUTE);
  if (afternoon.getTime() - now >= 30 * MINUTE)
    out.push({
      id: "afternoon",
      label: `This afternoon at ${clockWords(afternoon.getTime(), timeZone)}`,
      at: afternoon.toISOString(),
    });
  const tomorrow = iso(addDays(today, 1), MORNING_MINUTE);
  out.push({
    id: "tomorrow",
    label: `Tomorrow at ${clockWords(tomorrow.getTime(), timeZone)}`,
    at: tomorrow.toISOString(),
  });
  const toMonday = (8 - today.weekday) % 7 || 7;
  if (toMonday > 1) {
    const monday = iso(addDays(today, toMonday), MORNING_MINUTE);
    out.push({
      id: "monday",
      label: capitalize(sendAtWords(monday.toISOString(), now, timeZone)),
      at: monday.toISOString(),
    });
  }
  return out;
}

/** Why a chosen time can't be used; null when it can ("" is a time still being picked). */
export function sendAtProblem(at: string | null, now: number): string | null {
  if (at === null) return null;
  if (!at) return "Pick when it goes.";
  const ms = Date.parse(at);
  if (Number.isNaN(ms)) return "That isn't a date and time.";
  if (ms - now < MIN_LEAD_MS) return "Pick a time a little later than now.";
  if (ms - now > MAX_AHEAD_DAYS * DAY) return "Pick a time within the next year.";
  return null;
}

/** The `<input type="datetime-local">` value of an instant on the owner's clock. */
export function wallInputValue(at: string, timeZone: string): string {
  const p = localParts(Date.parse(at), timeZone);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${p.year}-${two(p.month)}-${two(p.day)}T${two(p.hour)}:${two(p.minute)}`;
}

/** A `datetime-local` value read on the owner's clock, as an ISO instant (null if unreadable). */
export function fromWallInput(value: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const d = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  return new Date(zonedTimeToUtc(d, Number(m[4]) * 60 + Number(m[5]), timeZone)).toISOString();
}

/** The fields chrono reads from a phrase (a narrow view of its result, for testing). */
export interface ReadWhen {
  date: LocalDate;
  /** Minute of the day, when the phrase gave a time. */
  minute: number | null;
}

/** The instant a read phrase names on the owner's clock (8:30 when it gave no time). */
export function instantOf(read: ReadWhen, timeZone: string): string {
  return new Date(zonedTimeToUtc(read.date, read.minute ?? MORNING_MINUTE, timeZone)).toISOString();
}

/**
 * Reads a typed phrase ("tomorrow 8:30", "fri 4pm", "next monday") on the owner's clock, with
 * chrono loaded on first use (its own chunk). Null when it names no time ahead.
 */
export async function parseWhen(
  text: string,
  now: number,
  timeZone: string,
): Promise<string | null> {
  const words = text.trim();
  if (!words) return null;
  const chrono = await import("chrono-node");
  const ref = { instant: new Date(now), timezone: offsetMs(now, timeZone) / MINUTE };
  const parsed = chrono.parse(words, ref, { forwardDate: true })[0];
  if (!parsed) return null;
  const s = parsed.start;
  const read: ReadWhen = {
    date: { year: s.get("year")!, month: s.get("month")!, day: s.get("day")! },
    minute: s.isCertain("hour") ? s.get("hour")! * 60 + (s.get("minute") ?? 0) : null,
  };
  const at = instantOf(read, timeZone);
  return Date.parse(at) > now ? at : null;
}
